import styled from 'styled-components'
import { Button as AntButton, Badge as AntBadge } from 'antd'
import {
  BORDER_RADIUS,
  FONT_WEIGHT,
  SPACING,
  TRANSITIONS,
  TYPOGRAPHY,
} from '../../../../styles/theme'
import {
  NOTIFICATION_DROPDOWN_MAX_HEIGHT,
  NOTIFICATION_DROPDOWN_WIDTH,
} from '../../../../constants/notifications'

// Bell trigger button — matches the icon-button rhythm of other header actions.
export const BellTriggerButton = styled(AntButton)`
  &.ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

export const BellBadge = styled(AntBadge)`
  & .ant-badge-count,
  & .ant-badge-dot {
    box-shadow: 0 0 0 2px var(--color-bg-elevated);
  }
`

// Dropdown surface — mirrors the elevation + radius of UserMenu's container.
export const DropdownSurface = styled.div`
  width: ${NOTIFICATION_DROPDOWN_WIDTH}px;
  background-color: var(--color-bg-elevated);
  border-radius: ${BORDER_RADIUS.lg};
  box-shadow: var(--shadow-dropdown);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

export const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${SPACING.md} ${SPACING.lg};
  border-bottom: 1px solid var(--color-border);
`

export const DropdownTitle = styled.span`
  font-size: ${TYPOGRAPHY.body.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
`

export const HeaderAction = styled(AntButton)`
  &.ant-btn {
    padding: 0;
    height: auto;
    font-size: ${TYPOGRAPHY.caption.fontSize};
    font-weight: ${FONT_WEIGHT.medium};
  }
`

export const ItemList = styled.div`
  max-height: ${NOTIFICATION_DROPDOWN_MAX_HEIGHT}px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`

interface ItemRowProps {
  $unread: boolean
  $clickable: boolean
}

export const ItemRow = styled.div<ItemRowProps>`
  position: relative;
  display: flex;
  gap: ${SPACING.md};
  padding: ${SPACING.md} ${SPACING.lg};
  border-bottom: 1px solid var(--color-border);
  transition: background-color ${TRANSITIONS.fast};
  cursor: ${(props) => (props.$clickable ? 'pointer' : 'default')};
  background-color: ${(props) => (props.$unread ? 'var(--color-selected-bg)' : 'transparent')};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: var(--color-bg-hover);
  }
`

export const ItemIconContainer = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${BORDER_RADIUS.full};
  background-color: var(--color-bg-hover);
  color: var(--color-text-secondary);
`

export const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs};
`

export const ItemHeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${SPACING.sm};
`

export const ItemTitle = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const ItemTimestamp = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  color: var(--color-text-tertiary);
  transition: opacity ${TRANSITIONS.fast};

  ${ItemRow}:hover & {
    opacity: 0;
  }
`

export const ItemText = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  line-height: 1.4;
  word-break: break-word;
`

export const DismissButton = styled(AntButton)`
  &.ant-btn {
    position: absolute;
    top: ${SPACING.sm};
    right: ${SPACING.sm};
    opacity: 0;
    transition: opacity ${TRANSITIONS.fast};
  }

  ${ItemRow}:hover &.ant-btn {
    opacity: 1;
  }
`

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.sm};
  padding: ${SPACING.xxl} ${SPACING.lg};
  text-align: center;
  color: var(--color-text-secondary);
`

export const EmptyTitle = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-primary);
`

export const EmptySubtitle = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-tertiary);
`
